import React, {useState} from 'react';
import {ExternalLinkIcon, LinkIcon, UnlinkIcon} from 'lucide-react';
import CanvasController from './CanvasController.ts';
import AddLinkModal from './AddLinkModal.tsx';
import {ToolButton, Toolbar} from '../ds/ToolFrame.tsx';

interface ImageToolbarProps {
    controlsDisabled: boolean;
    controller: CanvasController;
}

/*
 * Contextual controls for the selected picture: turning it into a link.
 * "Open link" is here for the same reason as on a resource card, since a
 * single click only follows the link in read-only mode.
 */
const ImageToolbar: React.FC<ImageToolbarProps> = ({
    controlsDisabled,
    controller
}) => {
    const [showLinkModal, setShowLinkModal] = useState(false);

    const link = controller.activeLinkableImage?.link ?? '';
    const isLinked = link !== '';

    const onLinkEntered = (url: string) => {
        controller.setImageLink(url);
        setShowLinkModal(false);
    };

    return (
        <>
            {showLinkModal && (
                <AddLinkModal
                    heading="Link this picture"
                    confirmLabel={isLinked ? 'Save' : 'Add'}
                    initialUrl={link}
                    showTitle={false}
                    onClose={() => setShowLinkModal(false)}
                    onLinkEntered={onLinkEntered}
                />
            )}
            <Toolbar label="Picture" className="whiteboard-toolbar">
                <ToolButton
                    label={isLinked ? 'Change link' : 'Add link'}
                    icon={LinkIcon}
                    disabled={controlsDisabled}
                    onClick={() => setShowLinkModal(true)}
                />
                {isLinked && (
                    <>
                        <ToolButton
                            label="Open link"
                            icon={ExternalLinkIcon}
                            disabled={controlsDisabled}
                            onClick={() => controller.openActiveImageLink()}
                        />
                        <ToolButton
                            label="Remove link"
                            icon={UnlinkIcon}
                            disabled={controlsDisabled}
                            onClick={() => controller.removeImageLink()}
                        />
                    </>
                )}
            </Toolbar>
        </>
    );
};

export default ImageToolbar;
